/**
 * The one name a room is allowed to be told for a person (QA iteration 3, UX-400 / UX-301).
 *
 * A pairing used to store `displayName ?? email.split('@')[0]`, so an operator whose address is
 * `carlos+dev@smartbit.co` was greeted as "Hola, carlos+dev" on the phone, credited as
 * "Enviada por carlos+dev" on the TV and listed that way in every participant list. Iteration 2
 * fixed the derivation — and then iteration 3 found the same string everywhere again, because
 * the old derivation had already been **persisted**: `users.display_name`, the membership row
 * and every `session_songs.added_by_name` written before the fix hold `carlos+dev`, and a
 * function that returns a stored display name verbatim faithfully hands it back.
 *
 * So the rule has two halves, and this module is the only place either of them lives:
 *
 *   1. a display name wins — unless it is itself an address fragment. A name carrying `@` or
 *      `+`, or one that is exactly the local part of the address it belongs to, is the old
 *      derivation rather than a name anybody chose, and falls through;
 *   2. otherwise the local part **before `+` or `@`**, capitalised (`carlos+dev@x.com` →
 *      `Carlos`);
 *   3. otherwise the empty string — the caller prints its own "Invitado", never an address.
 *
 * Only the first letter is touched: `María José` and `McCarthy` must survive untouched, and a
 * dotted local part stays one word (`ana.maria@x.com` → `Ana.maria`) — the dots inside a local
 * part are not reliable word boundaries to guess at.
 *
 * Migration `0010_person_names.sql` clears the rows the old code poisoned; this guard is what
 * keeps a row written by an older deploy — or by a user who types their own address into the
 * name field — from reaching a living-room screen in the meantime.
 */

function clean(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

/** Everything before the first `+` or `@`. */
export function emailLocalName(email: string | null | undefined): string {
  const head = clean(email).split('@')[0] ?? '';
  return (head.split('+')[0] ?? '').trim();
}

/**
 * True when `name` is not a name at all but the address it belongs to, or a fragment of one.
 * Exported so a UI can refuse to *offer* such a value (the phone's name editor pre-fills with
 * the stored `display_name`).
 */
export function looksLikeAddress(name: string | null | undefined, email?: string | null): boolean {
  const value = clean(name);
  if (!value) return false;
  if (value.includes('@') || value.includes('+')) return true;
  const local = clean(email).split('@')[0] ?? '';
  return local.length > 0 && value.toLowerCase() === local.toLowerCase();
}

/**
 * The name to print for a person, or `''` when there is nothing printable. See the module
 * comment for the rule.
 */
export function personName(displayName?: string | null, email?: string | null): string {
  const given = clean(displayName);
  if (given && !looksLikeAddress(given, email)) return given;

  // A stored name that *is* an address ("carlos+dev@smartbit.co" typed into the field) is still
  // the best clue to who this is when the account carries no e-mail of its own.
  const local = emailLocalName(email) || emailLocalName(given);
  if (!local) return '';
  return local.charAt(0).toUpperCase() + local.slice(1);
}
